import { endpoints } from '@api'
import {
  Box,
  Button,
  Container,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react'
import { DeleteConfirmation } from '@components'
import { NoteSettings } from '@components/icons'
import { useSessionManagement } from '@context'
import { useExperienceManager } from '@hooks'
import { MagicEditIcon, TrashIcon } from '@icons'
import { Lock } from '@material-ui/icons'
import React, { useEffect, useRef, useState } from 'react'
import { isChrome, isDesktop, isMobile } from 'react-device-detect'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link as RouterLink } from 'react-router-dom'
import {
  getOrganizationControllerGetSessionCreditQueryKey,
  getSessionControllerGetProgressNotesForSessionQueryKey
} from '~/clinician-api'
import { ProgressNoteExplanation } from '.'
import LowWordCountBanner from './low-word-count-banner'
import { MagicEditDrawer } from './magic-edit-drawer'
import NoteBox from './note-box'
import {
  AppliedMemory,
  MemoryManagementDrawer
} from './progress-note-template-memory'

interface SessionTranscriptResponse {
  sessionTranscript: {
    id: string
  } | null
}

interface ProgressNoteProps {
  handleTrackEvent: (event: string, properties?: any) => void
  isNext?: boolean
  staticHeight?: boolean
  emptyNote: boolean
  transcriptionError: boolean | undefined
  uploadAlert: () => React.ReactNode
  showNotePreferencesText: boolean | undefined
  buildHeader: () => React.ReactNode
  buildSubHeader: () => React.ReactNode
  isDocumentationAutomationEnabled: boolean
  isTranscriptTooShort: boolean | undefined
  handleChangeNoteTypeClick: () => void
  openNoteSettingsDrawer: () => void
  buttonSpacing: string | undefined
  isIntegratedUI: boolean
  onStartNewDictationSessionClick: () => void
  user: any
  noteType: string
}

const ProgressNote: React.FC<ProgressNoteProps> = ({
  handleTrackEvent,
  isNext,
  staticHeight,
  emptyNote,
  transcriptionError,
  uploadAlert,
  showNotePreferencesText,
  buildHeader,
  buildSubHeader,
  isDocumentationAutomationEnabled,
  isTranscriptTooShort,
  handleChangeNoteTypeClick,
  openNoteSettingsDrawer,
  buttonSpacing,
  isIntegratedUI,
  onStartNewDictationSessionClick,
  user,
  noteType
}) => {
  const {
    progressNote,
    isNoteLocked,
    progressNoteData,
    sessionTranscriptData,
    sessionTranscriptLoading,
    session
  } = useSessionManagement()
  const {
    id,
    note: v1Note,
    nextNote: v2Note,
    noteType: progressNoteType,
    template,
    lockedAt
  } = progressNote

  const progressNotes = progressNoteData?.sessionProgressNotes || []

  const {
    isExtension,
    isWidget,
    notePreferencesEnabled,
    memoryEnabled,
    isAdmin
  } = useExperienceManager()

  const [isMemoryManagementDrawerOpen, setIsMemoryManagementDrawerOpen] =
    useState(false)
  const [isMagicEditDrawerOpen, setIsMagicEditDrawerOpen] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)

  const toast = useToast()
  const queryClient = useQueryClient()
  const organizationId = user?.clinic?.organization?.id

  const buttonsHaveTextBreakpoint = useBreakpointValue({
    base: false,
    sm: true
  })

  const [parentWidth, setParentWidth] = useState(0)
  const parentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    queryClient.invalidateQueries(
      getOrganizationControllerGetSessionCreditQueryKey(
        user?.clinic?.organization_id
      )
    )
  }, [])

  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setParentWidth(parentRef.current.offsetWidth)
      }
    }

    window.addEventListener('resize', updateWidth)
    updateWidth()

    return () => window.removeEventListener('resize', updateWidth)
  }, [])
  const overrideButtonsHaveText = !parentRef?.current || parentWidth > 600
  const buttonsHaveText = buttonsHaveTextBreakpoint && overrideButtonsHaveText

  const { data: magicEditSessionTranscriptData } =
    useQuery<SessionTranscriptResponse>(
      [endpoints.getSessionTranscript.getCacheId(), progressNote.sessionId],
      () =>
        endpoints.getSessionTranscript.request({
          sessionId: progressNote.sessionId
        }) as Promise<SessionTranscriptResponse>
    )

  const { data: progressNoteSettingsData } = useQuery<{
    transcriptsEnabled: boolean
    preferences: Record<string, any>
  }>(
    [endpoints.getProgressNoteSettings.getCacheId(), organizationId],
    () =>
      endpoints.getProgressNoteSettings.request({ organizationId }) as Promise<{
        transcriptsEnabled: boolean
        preferences: Record<string, any>
      }>
  )

  const {
    mutate: deleteSessionProgressNotes,
    isLoading: isDeletingSessionProgressNotes
  } = useMutation(endpoints.deleteSessionProgressNotes.request, {
    onSuccess: async () => {
      setIsDeleteConfirmationOpen(false)
      // eslint-disable-next-line
      parent.postMessage(
        {
          type: 'BP_DELETE_NOTE_CLICKED',
          sessionId: progressNote.sessionId,
          progressNoteId: id
        },
        '*'
      )
      toast({
        description: 'Notes deleted!',
        status: 'success',
        isClosable: true,
        duration: 2000
      })
      await queryClient.invalidateQueries(
        getSessionControllerGetProgressNotesForSessionQueryKey(
          progressNote.sessionId
        )
      )
    }
  })

  const transcriptsEnabled = progressNoteSettingsData?.transcriptsEnabled

  const isMagicEditEnabled =
    !!magicEditSessionTranscriptData?.sessionTranscript?.id &&
    isDocumentationAutomationEnabled &&
    !isNoteLocked

  const renderMagicEditPopoverBody = () => {
    if (transcriptsEnabled) {
      return ''
    }

    if (isAdmin) {
      return (
        <Flex>
          <Text>
            Turn on transcript retention in{' '}
            <Link
              as={RouterLink}
              to="/settings/progress-notes"
              color="primary"
              _hover={{ color: 'primary' }}
              target="_blank"
              _focus={{ outline: 'none' }}
            >
              settings
            </Link>
            .
          </Text>
        </Flex>
      )
    }

    return <Text>Ask your admin to turn on transcript retention.</Text>
  }

  const renderMagicEditButton = () => (
    <Button
      id={`pendo-magic-edit-button`}
      borderRadius="4px"
      onClick={() => setIsMagicEditDrawerOpen(true)}
      variant="outline"
      m={`0 0 0 ${buttonSpacing}`}
      h="35px"
      borderColor="pale_gray"
      {...(!buttonsHaveText ? { w: '35px', minW: '35px', maxW: '35px' } : {})}
      disabled={!isMagicEditEnabled}
      _hover={{ opacity: isMagicEditEnabled ? 0.7 : 'none' }}
      _focus={{ outline: 'none' }}
      data-click-text="Magic edit note"
    >
      <Flex align="center" justify="space-between">
        <MagicEditIcon />
        {buttonsHaveText && <Text ml="5px">Magic edit</Text>}
      </Flex>
    </Button>
  )

  const renderDeleteButton = () => (
    <Button
      variant="outline"
      borderRadius="4px"
      m={`0 0 0 ${buttonSpacing}`}
      h="35px"
      borderColor="pale_gray"
      {...(!buttonsHaveText ? { w: '35px', minW: '35px', maxW: '35px' } : {})}
      onClick={() => setIsDeleteConfirmationOpen(true)}
      {...(isIntegratedUI ? { padding: '8px' } : {})}
      data-click-text="Delete note"
    >
      <Flex align="center" justify="space-between" gap="8px">
        <TrashIcon fill="#EB5164" />
        {buttonsHaveText && <Text>Delete</Text>}
      </Flex>
    </Button>
  )

  const note = isNext ? v2Note : v1Note

  const isSafariOrChrome = isMobile && !isDesktop ? (isChrome ? 107 : 85) : 0
  const notePreferencesBaseHeight = showNotePreferencesText
    ? 205
    : 200 + isSafariOrChrome

  return (
    <Container
      height={
        isExtension || isWidget
          ? 'calc(100vh - 165px)'
          : `calc(100vh - ${notePreferencesBaseHeight}px)`
      }
      overflowY="scroll"
      marginTop="0px !important"
      ref={parentRef}
    >
      <Flex flexDirection="column" flex="1">
        {(transcriptionError || isTranscriptTooShort) && (
          <Box mt="16px">
            <LowWordCountBanner
              onStartNewDictationSessionClick={onStartNewDictationSessionClick}
            />
          </Box>
        )}
        <Flex justifyContent="space-between" pt="16px">
          <Flex
            flexDirection="column"
            mb={showNotePreferencesText ? '24px' : '16px'}
          >
            <Box>
              <Text as="h2" fontSize="24px" fontWeight="bold">
                {buildHeader()}
              </Text>
            </Box>

            <Flex alignItems="center">
              <Text as="h1" fontWeight="430">
                {buildSubHeader()}
              </Text>
              {isDocumentationAutomationEnabled && !sessionTranscriptLoading ? (
                sessionTranscriptData?.sessionTranscript?.id ? (
                  <Button
                    color="primary"
                    onClick={handleChangeNoteTypeClick}
                    variant="link"
                    textDecoration="none"
                    disabled={
                      isTranscriptTooShort || transcriptionError || !!lockedAt
                    }
                    _focus={{ outline: 'none' }}
                    id="pendo-change-note-type"
                    data-click-text="Change note type"
                  >
                    Change
                  </Button>
                ) : (
                  <Popover
                    trigger="hover"
                    placement={isIntegratedUI ? 'bottom' : 'top'}
                  >
                    <PopoverContent
                      _focus={{ outline: 'none' }}
                      borderColor="pale_gray"
                    >
                      <PopoverArrow />
                      <PopoverBody>
                        Changing the note type requires a transcript.
                      </PopoverBody>
                    </PopoverContent>
                    {/* @ts-ignore */}
                    <PopoverTrigger>
                      <Button
                        as="div"
                        variant="link"
                        disabled
                        textDecoration="none"
                        color="disabled"
                      >
                        Change
                      </Button>
                    </PopoverTrigger>
                  </Popover>
                )
              ) : null}
            </Flex>
          </Flex>
          <Flex alignItems="flex-start" gap="0">
            {isNoteLocked ? (
              <Flex alignItems="center" gap="8px">
                <Text color="gray.500">Note is locked</Text>
                <Box
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="4px"
                  w="24px"
                  h="24px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Lock style={{ fontSize: '14px' }} />
                </Box>
              </Flex>
            ) : (
              <>
                {/* Magic Edit Button */}
                {isMagicEditEnabled
                  ? renderMagicEditButton()
                  : isDocumentationAutomationEnabled && (
                      <Popover trigger="hover" placement="top">
                        <PopoverContent
                          _focus={{ outline: 'none' }}
                          borderColor="pale_gray"
                        >
                          <PopoverArrow />
                          <PopoverBody>
                            Magic Edit requires a transcript.
                            {renderMagicEditPopoverBody()}
                          </PopoverBody>
                        </PopoverContent>
                        {/* @ts-ignore */}
                        <PopoverTrigger>
                          <div>{renderMagicEditButton()}</div>
                        </PopoverTrigger>
                      </Popover>
                    )}
                {/* Delete Button */}
                {renderDeleteButton()}
              </>
            )}

            {/* Note Preferences Button */}
            {isDocumentationAutomationEnabled &&
            notePreferencesEnabled &&
            !memoryEnabled ? (
              sessionTranscriptData?.sessionTranscript?.id ? (
                <Button
                  data-id="note-preferences-button"
                  variant="outline"
                  borderRadius="4px"
                  borderColor="pale_gray"
                  m={`0 0 0 ${buttonSpacing}`}
                  pl={!buttonsHaveText ? '0px' : '8px'}
                  pr={!buttonsHaveText ? '0px' : '8px'}
                  h="35px"
                  {...(!buttonsHaveText
                    ? {
                        w: '35px',
                        minW: '35px',
                        maxW: '35px'
                      }
                    : {})}
                  onClick={openNoteSettingsDrawer}
                  {...(isIntegratedUI ? { padding: '8px' } : {})}
                  data-click-text="Note preferences"
                >
                  <Flex align="center" justify="space-between" gap="8px">
                    <NoteSettings fill="#282828" />
                    {buttonsHaveText && <Text>Note preferences</Text>}
                  </Flex>
                </Button>
              ) : (
                <Popover
                  trigger="hover"
                  placement={isIntegratedUI ? 'bottom' : 'top'}
                >
                  <PopoverContent
                    _focus={{ outline: 'none' }}
                    borderColor="pale_gray"
                  >
                    <PopoverArrow />
                    <PopoverBody>
                      Changing note settings requires a transcript.
                    </PopoverBody>
                  </PopoverContent>
                  {/* @ts-ignore */}
                  <PopoverTrigger>
                    <Button
                      data-id="note-preferences-button"
                      as="div"
                      variant="outline"
                      h="35px"
                      disabled
                      borderColor="pale_gray"
                      borderRadius="4px"
                      textDecoration="none"
                      color="disabled"
                      pl={!showNotePreferencesText ? '0px' : '8px'}
                      pr={!showNotePreferencesText ? '0px' : '8px'}
                    >
                      <Flex align="center" justify="space-between" gap="8px">
                        <NoteSettings fill="#282828" />
                        {buttonsHaveText && <Text>Note preferences</Text>}
                      </Flex>{' '}
                    </Button>
                  </PopoverTrigger>
                </Popover>
              )
            ) : null}
          </Flex>
        </Flex>
        {uploadAlert && <Box>{uploadAlert()}</Box>}
        {Boolean(memoryEnabled && progressNote.memories?.data?.length) && (
          <Box>
            <AppliedMemory
              memories={progressNote.memories?.data}
              invalidMemories={progressNote.memories?.invalid}
              onManageClick={() => setIsMemoryManagementDrawerOpen(true)}
              isDisabled={progressNote.isMemoryDisabled}
              noteTypeLabel={template.displayShort || progressNoteType}
            />
          </Box>
        )}

        {template.sections.map((section: any) => (
          <NoteBox
            noteId={id}
            key={`${progressNote.id}-${section.key}`}
            type={progressNoteType}
            field={section.key}
            isEditable={!isNext}
            buttonId={`pendo-copy-${progressNoteType}-${section.key}-tag`}
            label={section.displayName}
            value={note[section.key] || ''}
            emptyMessage={`No ${section.key} note`}
            trackEvent={handleTrackEvent}
            isNext={isNext}
            staticHeight={staticHeight}
            disabled={isNoteLocked}
            isNoteLocked={isNoteLocked}
          />
        ))}
        {!emptyNote && (
          <Box mb="16px">
            <ProgressNoteExplanation />
          </Box>
        )}
      </Flex>

      {isMemoryManagementDrawerOpen && (
        <Drawer
          isOpen={isMemoryManagementDrawerOpen}
          onClose={() => setIsMemoryManagementDrawerOpen(false)}
          size="md"
        >
          <DrawerOverlay />
          <DrawerContent>
            <MemoryManagementDrawer
              onClose={() => setIsMemoryManagementDrawerOpen(false)}
              noteType={progressNoteType}
              noteTypeLabel={template?.displayShort || progressNoteType}
              sessionId={progressNote.sessionId}
            />
          </DrawerContent>
        </Drawer>
      )}

      <MagicEditDrawer
        isOpen={isMagicEditDrawerOpen}
        closeDrawer={() => setIsMagicEditDrawerOpen(false)}
        progressNoteId={id}
        openSettings={openNoteSettingsDrawer}
        noteType={progressNotes[0]?.noteType}
        noteTypeLabel={noteType}
        preferences={{
          ...(progressNoteSettingsData?.preferences || {}),
          ...(progressNote?.preferences || {})
        }}
        session={session}
        progressNote={progressNote}
      />

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        isConfirming={isDeletingSessionProgressNotes}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={() =>
          deleteSessionProgressNotes({ id: progressNote.sessionId })
        }
        header="Delete this note?"
        text="This note (and all associated versions of the note) will be deleted."
        confirmText="Delete note"
      />
    </Container>
  )
}

export default ProgressNote
